import { useState, useEffect } from 'react';
import { usersApi, type UserWithDetails } from '../services/api/endpoints/users';

export type { UserWithDetails };

export function useUsers() {
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await usersApi.getUsers();
      
      if (response.success && response.data) {
        setUsers(response.data.users);
      } else {
        // Fallback vers les données mockées en cas d'erreur API
        const mockUsers: UserWithDetails[] = [
          {
            id: '1',
            email: 'admin@kiota.com',
            name: 'Super Administrateur',
            role: 'superadmin',
            department: 'Direction',
            lastLogin: '2024-03-01T15:30:00Z',
            status: 'active',
            avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
          },
          {
            id: '2',
            email: 'comptable@kiota.com',
            name: 'Jean Dupont',
            role: 'user',
            department: 'Comptabilité',
            lastLogin: '2024-03-01T14:45:00Z',
            status: 'active',
            avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
          }
        ];
        setUsers(mockUsers);
        setError('API non disponible, utilisation des données de démonstration');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des utilisateurs:', err);
      setError('Erreur lors du chargement des utilisateurs');
      
      // Fallback vers les données mockées
      const mockUsers: UserWithDetails[] = [
        {
          id: '1',
          email: 'admin@kiota.com',
          name: 'Super Administrateur',
          role: 'superadmin',
          department: 'Direction',
          lastLogin: '2024-03-01T15:30:00Z',
          status: 'active',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
        },
        {
          id: '2',
          email: 'comptable@kiota.com',
          name: 'Jean Dupont',
          role: 'user',
          department: 'Comptabilité',
          lastLogin: '2024-03-01T14:45:00Z',
          status: 'active',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
        }
      ];
      setUsers(mockUsers);
    } finally {
      setLoading(false);
    }
  };

  const addUser = async (userData: Partial<UserWithDetails>) => {
    try {
      const response = await usersApi.createUser({
        email: userData.email!,
        firstName: userData.name?.split(' ')[0] || '',
        lastName: userData.name?.split(' ').slice(1).join(' ') || '',
        role: userData.role === 'superadmin' ? 'admin' : (userData.role as any) || 'viewer',
        department: userData.department!
      });

      if (response.success && response.data) {
        setUsers(prev => [...prev, response.data!]);
        return response.data;
      } else {
        throw new Error(response.error || 'Erreur lors de la création de l\'utilisateur');
      }
    } catch (err) {
      console.error('Erreur lors de l\'ajout de l\'utilisateur:', err);
      
      // Fallback vers la création locale
      const newUser: UserWithDetails = {
        id: crypto.randomUUID(),
        email: userData.email!,
        name: userData.name!,
        role: userData.role || 'user',
        department: userData.department!,
        lastLogin: new Date().toISOString(),
        status: 'active',
        avatar: userData.avatar
      };

      setUsers(prev => [...prev, newUser]);
      return newUser;
    }
  };

  const updateUser = async (id: string, userData: Partial<UserWithDetails>) => {
    try {
      const response = await usersApi.updateUser(id, {
        firstName: userData.name?.split(' ')[0],
        lastName: userData.name?.split(' ').slice(1).join(' '),
        role: userData.role === 'superadmin' ? 'admin' : (userData.role as any),
        department: userData.department,
        status: userData.status,
        avatar: userData.avatar
      });

      if (response.success && response.data) {
        setUsers(prev => prev.map(user => 
          user.id === id ? response.data! : user
        ));
        return response.data;
      } else {
        throw new Error(response.error || 'Erreur lors de la mise à jour de l\'utilisateur');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'utilisateur:', err);
      
      // Fallback vers la mise à jour locale
      setUsers(prev => prev.map(user => 
        user.id === id ? { ...user, ...userData } : user
      ));
    }
  };

  const toggleUserStatus = async (id: string) => {
    try {
      const response = await usersApi.toggleUserStatus(id);

      if (response.success && response.data) {
        setUsers(prev => prev.map(user => 
          user.id === id ? response.data! : user
        ));
        return response.data;
      } else {
        throw new Error(response.error || 'Erreur lors du changement de statut');
      }
    } catch (err) {
      console.error('Erreur lors du changement de statut:', err);
      
      // Fallback vers la mise à jour locale
      setUsers(prev => prev.map(user => 
        user.id === id 
          ? { ...user, status: user.status === 'active' ? 'inactive' : 'active' }
          : user
      ));
    }
  };

  return {
    users,
    loading,
    error,
    addUser,
    updateUser,
    toggleUserStatus,
    refreshUsers: loadUsers
  };
}